import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Card,
  Typography,
  DatePicker,
  Select,
  Table,
  Row,
  Col,
  Statistic,
  Space,
  Divider,
  Segmented,
  Tooltip,
  Empty,
  Spin,
  App
} from 'antd'
import styled from 'styled-components'
import api from '../../utils/api'
import Hint from '../../components/common/Hint'
import { now as tzNow, fromPicker } from '../../utils/timezone'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

/**
 * 前台轉換漏斗與顧客來源報表（平台管理員）。
 *
 * 資料來自 beauty-server 的 FunnelEvent（前台埋點）與 Booking：
 *   GET /api/track/funnel   每一階段的預約嘗試數與逐段轉換率
 *   GET /api/track/sources  每個來源帶來幾筆單、多少實收營收
 *
 * 分母刻意用 flowId（一次預約嘗試）而不是人頭：
 * 同一個人一天看三家店＝三次機會，用人頭會低估掉單率。
 */

// 後台既有的玫瑰色調（與 components/common/Hint.js 同一組），
// 漏斗只有單一數列，所以用「同一個色相的深淺」表達大小，不用多色。
const ROSE = '#d5728a'
const ROSE_SOFT = '#f9e8ee'
const INK = '#5a5a5a'
const MUTED = '#8a8a8a'
const DANGER = '#c86e6e'

const Bar = styled.div`
  position: relative;
  height: 30px;
  background: ${ROSE_SOFT};
  border-radius: 4px;
  overflow: hidden;
`

const Fill = styled.div`
  height: 100%;
  background: ${ROSE};
  border-radius: 4px 0 0 4px;
  transition: width 0.4s ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const BarLabel = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  padding: 0 10px;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: ${INK};
  gap: 10px;
  pointer-events: none;
`

const StepRow = styled.div`
  display: grid;
  grid-template-columns: 190px 1fr 96px 92px;
  gap: 12px;
  align-items: center;
  padding: 6px 0;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 4px;
    padding: 10px 0;
    border-bottom: 1px solid #f0ede5;
  }
`

const StepName = styled.div`
  font-size: 13px;
  color: ${INK};
  display: flex;
  gap: 8px;
  align-items: baseline;
`

const StepNum = styled.span`
  font-size: 12px;
  color: ${MUTED};
  font-variant-numeric: tabular-nums;
  min-width: 16px;
`

const Num = styled.div`
  font-size: 13px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: ${({ $danger }) => ($danger ? DANGER : MUTED)};

  @media (max-width: 900px) {
    text-align: left;
  }
`

const pct = (v) => (v == null ? '—' : `${v}%`)
const money = (v) => `$${Math.round(v || 0).toLocaleString('en-US')}`

const CHANNEL_LABELS = {
  paid_search: '付費搜尋',
  paid_social: '付費社群',
  organic_search: '自然搜尋',
  social: '自然社群',
  line: 'LINE',
  email: 'Email',
  crm: '簡訊／通知',
  referral: '外部連結',
  campaign: '自訂活動',
  google_reserve: 'Google 預訂',
  internal: '站內',
  direct: '直接進站',
  '(未記錄)': '(未記錄)'
}

const FunnelReportPage = () => {
  const { message } = App.useApp()

  const [range, setRange] = useState([
    tzNow().subtract(7, 'day').startOf('day'),
    tzNow().endOf('day')
  ])
  const [scope, setScope] = useState('platform')
  const [device, setDevice] = useState('all')
  const [businesses, setBusinesses] = useState([])

  const [funnelLoading, setFunnelLoading] = useState(false)
  const [funnel, setFunnel] = useState(null)

  const [sourceLoading, setSourceLoading] = useState(false)
  const [sources, setSources] = useState(null)
  const [sourceBy, setSourceBy] = useState('channel')
  const [sourceAttr, setSourceAttr] = useState('last')

  useEffect(() => {
    api
      .get('/api/admin/businesses', { params: { limit: 200 } })
      .then((res) => {
        if (res.data?.success) setBusinesses(res.data.data || [])
      })
      .catch(() => {
        /* 店家清單載入失敗不影響報表，維持「全平台」即可 */
      })
  }, [])

  const baseParams = useMemo(() => {
    const p = {
      from: fromPicker(range[0]).toISOString(),
      to: fromPicker(range[1]).toISOString()
    }
    if (scope !== 'platform') p.businessId = scope
    return p
  }, [range, scope])

  const fetchFunnel = useCallback(async () => {
    if (!range?.[0] || !range?.[1]) return
    setFunnelLoading(true)
    try {
      const params = { ...baseParams }
      if (device !== 'all') params.device = device
      const res = await api.get('/api/track/funnel', { params })
      if (res.data?.success) setFunnel(res.data)
      else message.error(res.data?.error || '載入漏斗失敗')
    } catch (e) {
      message.error(e.response?.data?.error || '載入漏斗失敗')
    } finally {
      setFunnelLoading(false)
    }
  }, [baseParams, device, range, message])

  const fetchSources = useCallback(async () => {
    if (!range?.[0] || !range?.[1]) return
    setSourceLoading(true)
    try {
      const res = await api.get('/api/track/sources', {
        params: { ...baseParams, by: sourceBy, attr: sourceAttr }
      })
      if (res.data?.success) setSources(res.data)
      else message.error(res.data?.error || '載入來源報表失敗')
    } catch (e) {
      message.error(e.response?.data?.error || '載入來源報表失敗')
    } finally {
      setSourceLoading(false)
    }
  }, [baseParams, sourceBy, sourceAttr, range, message])

  useEffect(() => {
    fetchFunnel()
  }, [fetchFunnel])

  useEffect(() => {
    fetchSources()
  }, [fetchSources])

  const steps = useMemo(() => funnel?.funnel || [], [funnel])
  const top = steps[0]?.flows || 0
  const done = steps[steps.length - 1]?.flows || 0

  // 流失最嚴重的一段。看的是「本段轉換率」而不是累積轉換率 ——
  // 累積數字會被前面每一段稀釋，看起來哪裡都在掉，找不出真正的破口。
  const worst = useMemo(() => {
    const candidates = steps.filter(
      (s) => s.step > 1 && s.stepConversion != null && s.dropFromPrev > 0
    )
    if (!candidates.length) return null
    return candidates.reduce((a, b) => (a.stepConversion <= b.stepConversion ? a : b))
  }, [steps])

  const reasonColumns = [
    {
      title: '事件',
      dataIndex: 'label',
      width: 140,
      render: (v, r) => v || r.event
    },
    { title: '原因', dataIndex: 'reason', render: (v) => v || '—' },
    {
      title: '次數',
      dataIndex: 'count',
      width: 90,
      align: 'right',
      sorter: (a, b) => a.count - b.count,
      defaultSortOrder: 'descend'
    }
  ]

  const maxRevenue = useMemo(
    () => Math.max(1, ...(sources?.rows || []).map((r) => r.revenue || 0)),
    [sources]
  )

  const sourceColumns = [
    {
      title: { channel: '管道', source: '來源', campaign: '廣告活動' }[sourceBy],
      dataIndex: 'key',
      render: (v) =>
        sourceBy === 'channel' ? CHANNEL_LABELS[v] || v : v || '(未記錄)'
    },
    {
      title: '進站',
      dataIndex: 'started',
      width: 84,
      align: 'right',
      sorter: (a, b) => a.started - b.started,
      render: (v) => v || '—'
    },
    {
      title: '預約',
      dataIndex: 'bookings',
      width: 84,
      align: 'right',
      sorter: (a, b) => a.bookings - b.bookings
    },
    {
      title: '取消',
      dataIndex: 'cancelled',
      width: 84,
      align: 'right',
      render: (v) => v || '—'
    },
    {
      title: '成交率',
      dataIndex: 'cvr',
      width: 92,
      align: 'right',
      sorter: (a, b) => (a.cvr || 0) - (b.cvr || 0),
      render: (v) => pct(v)
    },
    {
      title: '營收',
      dataIndex: 'revenue',
      width: 190,
      sorter: (a, b) => a.revenue - b.revenue,
      defaultSortOrder: 'descend',
      render: (v) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Bar style={{ height: 14, flex: 1, minWidth: 60 }}>
            <Fill style={{ width: `${Math.max(2, (v / maxRevenue) * 100)}%` }} />
          </Bar>
          <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: 74, textAlign: 'right' }}>
            {money(v)}
          </span>
        </div>
      )
    },
    {
      title: '客單價',
      dataIndex: 'aov',
      width: 100,
      align: 'right',
      render: (v) => money(v)
    },
    { title: '人數', dataIndex: 'customers', width: 78, align: 'right' }
  ]

  return (
    <div>
      <Title level={3} style={{ marginBottom: 4 }}>
        前台轉換與來源
      </Title>
      <Text type='secondary'>
        客人從看到服務到預約成立，每一步走掉多少人；以及每個來源帶來多少實收營收。
      </Text>

      {/* 篩選條件放同一列，三個報表共用 */}
      <Card size='small' style={{ marginTop: 16, marginBottom: 16 }}>
        <Space wrap size={12}>
          <RangePicker
            value={range}
            onChange={(v) => v && setRange([v[0].startOf('day'), v[1].endOf('day')])}
            allowClear={false}
            presets={[
              { label: '最近 7 天', value: [tzNow().subtract(7, 'day'), tzNow()] },
              { label: '最近 14 天', value: [tzNow().subtract(14, 'day'), tzNow()] },
              { label: '最近 30 天', value: [tzNow().subtract(30, 'day'), tzNow()] },
              { label: '最近 90 天', value: [tzNow().subtract(90, 'day'), tzNow()] }
            ]}
          />
          <Select
            value={scope}
            onChange={setScope}
            style={{ minWidth: 200 }}
            showSearch
            optionFilterProp='label'
            options={[
              { value: 'platform', label: '全平台' },
              ...businesses.map((b) => ({
                value: b._id,
                label: b.name || b.username
              }))
            ]}
          />
          <Segmented
            value={device}
            onChange={setDevice}
            options={[
              { label: '全部裝置', value: 'all' },
              { label: '手機', value: 'mobile' },
              { label: '桌機', value: 'desktop' }
            ]}
          />
        </Space>
      </Card>

      {/* ── 漏斗 ───────────────────────────────────────── */}
      <Card
        title='預約漏斗'
        style={{ marginBottom: 16 }}
        extra={
          <Text type='secondary' style={{ fontSize: 12 }}>
            分母為「預約嘗試」，非人頭
          </Text>
        }
      >
        <Spin spinning={funnelLoading}>
          {top === 0 ? (
            <Empty
              description='這個期間沒有資料。埋點剛上線的話，等客人走過一次預約流程就會出現。'
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <>
              <Row gutter={16} style={{ marginBottom: 20 }}>
                <Col xs={12} md={6}>
                  <Statistic title='預約嘗試' value={top} />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic title='完成預約' value={done} />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic
                    title='掉單率'
                    value={top ? ((top - done) / top) * 100 : 0}
                    precision={1}
                    suffix='%'
                    valueStyle={{ color: DANGER }}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic
                    title='整體成交率'
                    value={top ? (done / top) * 100 : 0}
                    precision={1}
                    suffix='%'
                    valueStyle={{ color: ROSE }}
                  />
                </Col>
              </Row>

              {worst && (
                <Hint
                  tone='warning'
                  style={{ marginBottom: 16 }}
                  title={`流失最嚴重的一段：${worst.label}（本段只有 ${worst.stepConversion}% 通過，少了 ${worst.dropFromPrev} 次嘗試）`}
                >
                  優先處理這一段，比同時改五個地方有效。下方「送出被擋下的原因」可以解釋為什麼掉。
                </Hint>
              )}

              <StepRow style={{ paddingBottom: 2 }}>
                <StepName style={{ color: MUTED, fontSize: 12 }}>步驟</StepName>
                <div style={{ color: MUTED, fontSize: 12 }}>次數</div>
                <Num style={{ fontSize: 12 }}>本段轉換</Num>
                <Num style={{ fontSize: 12 }}>累積轉換</Num>
              </StepRow>
              <Divider style={{ margin: '4px 0 8px' }} />

              {steps.map((s) => (
                <StepRow key={s.event}>
                  <StepName>
                    <StepNum>{s.step}</StepNum>
                    <span>{s.label}</span>
                  </StepName>
                  <Tooltip
                    title={`${s.label}：${s.flows} 次預約嘗試、${s.people} 位不重複訪客`}
                  >
                    <Bar>
                      <Fill
                        style={{ width: `${top ? Math.max(1, (s.flows / top) * 100) : 0}%` }}
                      />
                      <BarLabel>
                        <strong>{s.flows}</strong>
                        {s.dropFromPrev > 0 && (
                          <span style={{ color: DANGER }}>−{s.dropFromPrev}</span>
                        )}
                      </BarLabel>
                    </Bar>
                  </Tooltip>
                  <Num $danger={s.stepConversion != null && s.stepConversion < 70}>
                    {pct(s.stepConversion)}
                  </Num>
                  <Num>{pct(s.overallConversion)}</Num>
                </StepRow>
              ))}

              <Text type='secondary' style={{ fontSize: 12, display: 'block', marginTop: 14 }}>
                「被要求登入」只有未登入的客人會經過，所以它比上一段少是正常的，不代表流失。
                真正要盯的是「被要求登入 → 登入完成」與「送出預約 → 建立成功」這兩段。
              </Text>
            </>
          )}
        </Spin>
      </Card>

      {/* ── 掉單原因 ────────────────────────────────────── */}
      <Card title='送出被擋下 / 登入失敗的原因' style={{ marginBottom: 16 }}>
        <Table
          rowKey={(r) => `${r.event}-${r.reason}`}
          columns={reasonColumns}
          dataSource={funnel?.reasons || []}
          loading={funnelLoading}
          pagination={false}
          size='small'
          locale={{ emptyText: '這個期間沒有失敗紀錄' }}
        />
      </Card>

      {/* ── 標註事件：解釋上面每一段為什麼掉 ─────────────── */}
      {(funnel?.annotations || []).length > 0 && (
        <Card title='過程中的標註事件' style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            {funnel.annotations.map((a) => (
              <Col xs={12} sm={8} md={6} key={a.event}>
                <Statistic
                  title={
                    <span style={{ fontSize: 13 }}>{a.label || a.event}</span>
                  }
                  value={a.flows}
                  valueStyle={{ fontSize: 20 }}
                />
              </Col>
            ))}
          </Row>
          <Text type='secondary' style={{ fontSize: 12, display: 'block', marginTop: 12 }}>
            這些不算在主漏斗裡，是用來解釋漏斗為什麼掉。例如「放棄手機驗證」如果很高，
            代表驗證這一關本身就是流失來源，不是客人不想預約。
          </Text>
        </Card>
      )}

      {/* ── 來源 ───────────────────────────────────────── */}
      <Card
        title='顧客來源'
        extra={
          <Space wrap>
            <Segmented
              value={sourceBy}
              onChange={setSourceBy}
              options={[
                { label: '管道', value: 'channel' },
                { label: '來源', value: 'source' },
                { label: '廣告活動', value: 'campaign' }
              ]}
            />
            <Tooltip title='最後接觸＝這次預約前最後看到什麼（臨門一腳）；首次接觸＝當初是誰把人帶進來的（拉新）'>
              <Segmented
                value={sourceAttr}
                onChange={setSourceAttr}
                options={[
                  { label: '最後接觸', value: 'last' },
                  { label: '首次接觸', value: 'first' }
                ]}
              />
            </Tooltip>
          </Space>
        }
      >
        <Table
          rowKey='key'
          columns={sourceColumns}
          dataSource={sources?.rows || []}
          loading={sourceLoading}
          pagination={false}
          size='small'
          scroll={{ x: 900 }}
          locale={{ emptyText: '這個期間沒有預約資料' }}
          summary={(rows) => {
            if (!rows.length) return null
            const b = rows.reduce((a, r) => a + r.bookings, 0)
            const rev = rows.reduce((a, r) => a + r.revenue, 0)
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <strong>合計</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align='right' />
                <Table.Summary.Cell index={2} align='right'>
                  <strong>{b}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align='right' />
                <Table.Summary.Cell index={4} align='right' />
                <Table.Summary.Cell index={5}>
                  <strong style={{ display: 'block', textAlign: 'right' }}>
                    {money(rev)}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align='right' />
                <Table.Summary.Cell index={7} align='right' />
              </Table.Summary.Row>
            )
          }}
        />
        <Hint tone='info' style={{ marginTop: 16 }} title='這裡的營收是實收金額，不是廣告後台的估算值'>
          要算 ROAS，把某支廣告的花費除以這裡的營收即可。
          「(未記錄)」是埋點上線前的舊單，或直接輸入網址／從 LINE、IG 內建瀏覽器進來而沒有 referrer 的客人 —— 在台灣這一群本來就不小，屬於正常現象。
        </Hint>
      </Card>
    </div>
  )
}

export default FunnelReportPage
